import { useEffect, useMemo, useState } from 'react';
import { formatDate, formatTime } from '../../util/datetime';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ShareIcon from '@mui/icons-material/Share';
import { QRCodeSVG } from 'qrcode.react';

import { DeviceSharingDialog } from '../../components/DeviceSharingDialog';
import { SensorHealthDialog } from '../../components/SensorHealthDialog';

import {
  activeAdminTenantId,
  canWriteTenant,
  useAuth,
} from '../../auth/AuthContext';
import * as devicesApi from '../../api/devices';
import * as orgApi from '../../api/organizations';
import { SortHeader } from '../../components/SortHeader';
import { SearchableSelect } from '../../components/SearchableSelect';
import { sortRows, useTableSort } from '../../util/tableSort';
import { extractErrorMessage } from '../../api/errors';
import { RefreshButton } from '../../components/RefreshButton';
import { InstallCaptureAppPanel } from '../../components/InstallCaptureAppPanel';
import * as releasesApi from '../../api/captureAppReleases';
import * as appliancesApi from '../../api/appliances';
import { appVersionStatus, timeAgo, isOnline } from '../../util/deviceHealth';

const NONE_OPTION = [{ value: '', label: '— none —' }];

/** "android 16" → "Android 16"; joins os_name + os_version. */
function prettyOs(d: devicesApi.Device): string {
  if (!d.os_name) return '—';
  const name = d.os_name.charAt(0).toUpperCase() + d.os_name.slice(1);
  return `${name} ${d.os_version ?? ''}`.trim();
}

/**
 * Tenant-admin Devices page (ROADMAP #6 Case 1 — tenant-owned).
 *
 * Lists every device the tenant owns. Three controls per row:
 *
 *   - Edit (pencil) — rename the device label.
 *   - Suspend / Reactivate / Mark lost — status flips via a small
 *     menu. Retired devices show the chip and the inline controls
 *     are read-only (Retire is permanent within the UI; an ops
 *     ticket can revive one).
 *   - Retire (trash) — soft-delete that stamps status=retired. The
 *     row stays so historical sessions still resolve.
 *
 * Enrollment is a two-step handshake:
 *
 *   1. Operator clicks "Enroll device". The portal issues a 10-min
 *      code and renders it as both a QR (encoding the raw code) and
 *      the `XXXX-XXXX` text as a fallback.
 *   2. Operator opens the capture-app on the target device, scans
 *      the QR (or types the code if the camera struggles); the
 *      app sends back its public key and receives a device JWT.
 *
 * The portal never sees the public key — it sees the resulting
 * device row land in the list (refresh-on-close).
 */
export function TenantDevices() {
  const { user } = useAuth();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);

  const [devices, setDevices] = useState<devicesApi.Device[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Enrollment dialog state. No label field on this side — the
  // capture-app collects the device name (required there); we'd
  // only end up showing two inputs for the same value.
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollSubmitting, setEnrollSubmitting] = useState(false);
  const [enrollResult, setEnrollResult] =
    useState<devicesApi.EnrollmentToken | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  // Edit dialog state (label + default room).
  const [editing, setEditing] = useState<devicesApi.Device | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDept, setEditDept] = useState('');
  const [editLoc, setEditLoc] = useState('');
  const [editAppliance, setEditAppliance] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Departments + locations for the default-room selects.
  const [departments, setDepartments] = useState<orgApi.Department[]>([]);
  const [locations, setLocations] = useState<orgApi.Location[]>([]);
  const [appliances, setAppliances] = useState<appliancesApi.Appliance[]>([]);

  // Retire confirm.
  const [retiring, setRetiring] = useState<devicesApi.Device | null>(null);

  // Sensor-health dialog (ROADMAP CAPTURE-DEVICE-RATE-HISTORY). Read-only, so
  // available on every row including retired ones and for read-only admins.
  const [healthDevice, setHealthDevice] =
    useState<devicesApi.Device | null>(null);
  // Which device's cross-tenant sharing matrix is open
  // (DEVICE-CROSS-TENANT-SHARING).
  const [sharingDevice, setSharingDevice] =
    useState<devicesApi.Device | null>(null);
  const [retireSubmitting, setRetireSubmitting] = useState(false);

  const [refreshing, setRefreshing] = useState(false);
  // Retired devices are soft-deleted clutter — hidden by default so the list
  // shows the fleet that's actually in service. The switch reveals them.
  const [showRetired, setShowRetired] = useState(false);
  // The tenant's current capture-app version — devices behind it show
  // "Update available". Null when no release is configured (coloring stays
  // quiet then).
  const [currentAppVersion, setCurrentAppVersion] = useState<string | null>(
    null,
  );

  const sort = useTableSort<
    | 'label'
    | 'status'
    | 'room'
    | 'os'
    | 'app'
    | 'hardware'
    | 'last_seen'
    | 'enrolled'
  >('label');
  const deptById = useMemo(
    () => new Map(departments.map((d) => [d.id, d.name])),
    [departments],
  );
  const locById = useMemo(
    () => new Map(locations.map((l) => [l.id, l.name])),
    [locations],
  );
  // The device's default room: location name, else department name, else —.
  const roomLabel = (d: devicesApi.Device): string =>
    (d.default_location_id && locById.get(d.default_location_id)) ||
    (d.default_department_id && deptById.get(d.default_department_id)) ||
    '—';
  const sortedDevices = useMemo(() => {
    if (!devices) return [];
    return sortRows(
      devices,
      (d) => {
        switch (sort.orderBy) {
          case 'label':
            return d.label;
          case 'status':
            return d.status;
          case 'room': {
            const r = roomLabel(d);
            return r === '—' ? null : r;
          }
          case 'os':
            return d.os_name;
          case 'app':
            return d.app_version;
          case 'hardware':
            return d.hardware_id;
          case 'last_seen':
            return d.last_seen_at;
          case 'enrolled':
            return d.enrolled_at;
        }
      },
      sort.order,
    );
    // roomLabel closes over deptById/locById, tracked below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, sort.orderBy, sort.order, deptById, locById]);

  // What the table actually renders: retired devices are filtered out unless
  // the operator flips the switch to include them.
  const visibleDevices = useMemo(
    () =>
      showRetired
        ? sortedDevices
        : sortedDevices.filter((d) => d.status !== 'retired'),
    [sortedDevices, showRetired],
  );

  // At-a-glance fleet health from the list itself: lifecycle counts, plus
  // how many are behind the current app version or haven't checked in
  // recently. All derived — no fabricated health.
  const fleet = useMemo(() => {
    const c = { total: 0, active: 0, retired: 0, outdated: 0, offline: 0 };
    const now = new Date();
    for (const d of devices ?? []) {
      c.total += 1;
      if (d.status === 'active') c.active += 1;
      if (d.status === 'retired') c.retired += 1;
      // Version freshness only matters for a device that's in service — a
      // retired/suspended/lost one won't be updated, so don't nag about it.
      if (
        d.status === 'active' &&
        appVersionStatus(d.app_version, currentAppVersion) === 'outdated'
      ) {
        c.outdated += 1;
      }
      // "Offline" only means something for a device that's meant to be in
      // service — a retired one is off on purpose.
      if (d.status !== 'retired' && !isOnline(d.last_seen_at, now)) {
        c.offline += 1;
      }
    }
    return c;
  }, [devices, currentAppVersion]);

  const reload = async () => {
    if (!tenantId) return;
    setError(null);
    setRefreshing(true);
    try {
      const [rows, depts, locs, appls] = await Promise.all([
        devicesApi.listDevices(tenantId),
        orgApi.listDepartments(tenantId),
        orgApi.listLocations(tenantId),
        appliancesApi.listAppliances(tenantId).catch(() => []),
      ]);
      setDevices(rows);
      setDepartments(depts);
      setLocations(locs);
      setAppliances(appls);
      // Current app version for the "Update available" badge. Soft — a tenant
      // with no configured release just skips the version coloring.
      releasesApi
        .getCaptureAppInstaller(tenantId)
        .then((inst) => setCurrentAppVersion(inst.version))
        .catch(() => setCurrentAppVersion(null));
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load devices'));
      setDevices([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // While the QR is visible, poll the devices list. When a new
  // device row appears (the capture-app finished its enrollment
  // POST) auto-close the dialog and refresh — the operator
  // shouldn't have to watch the QR for the moment the staff member
  // taps Confirm on the device. Poll cadence is loose (2.5s) since
  // enrollment is a deliberate hand-off, not a millisecond-latency
  // path; the 10-minute code TTL means we can poll for a long time
  // without breaking anything.
  useEffect(() => {
    if (!enrollResult || !tenantId) return;
    const baseline = new Set((devices ?? []).map((d) => d.id));
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const rows = await devicesApi.listDevices(tenantId);
        if (cancelled) return;
        const newlyEnrolled = rows.find((r) => !baseline.has(r.id));
        if (newlyEnrolled) {
          setDevices(rows);
          setEnrollOpen(false);
          setEnrollResult(null);
        }
      } catch {
        // Swallow transient errors; the next tick will try again.
      }
    }, 2500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // `devices` intentionally NOT in the dep list — re-snapshotting
    // the baseline every time the list updates would defeat the
    // detection. We want the baseline frozen at the moment the QR
    // first appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollResult, tenantId]);

  const openEnroll = () => {
    setEnrollResult(null);
    setEnrollError(null);
    setEnrollOpen(true);
  };

  const submitEnroll = async () => {
    if (!tenantId) return;
    setEnrollSubmitting(true);
    setEnrollError(null);
    try {
      const result = await devicesApi.issueEnrollmentToken(tenantId);
      setEnrollResult(result);
    } catch (err: unknown) {
      setEnrollError(extractErrorMessage(err, 'failed to issue enrollment code'));
    } finally {
      setEnrollSubmitting(false);
    }
  };

  const closeEnroll = () => {
    if (enrollSubmitting) return;
    setEnrollOpen(false);
    if (enrollResult) void reload();
  };

  const openEdit = (d: devicesApi.Device) => {
    setEditing(d);
    setEditLabel(d.label);
    setEditDept(d.default_department_id ?? '');
    setEditLoc(d.default_location_id ?? '');
    setEditAppliance(d.appliance_id ?? '');
  };

  const submitEdit = async () => {
    if (!tenantId || !editing) return;
    setEditSubmitting(true);
    setError(null);
    try {
      await devicesApi.updateDevice(tenantId, editing.id, {
        label: editLabel.trim(),
        default_department_id: editDept || null,
        default_location_id: editLoc || null,
        // appliance pairing is device-controlled (set on the capture app), so
        // the portal never writes it — read-only here.
      });
      setEditing(null);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to update device'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const toggleStatus = async (
    d: devicesApi.Device,
    next: devicesApi.DeviceStatus,
  ) => {
    if (!tenantId) return;
    setError(null);
    try {
      await devicesApi.updateDevice(tenantId, d.id, { status: next });
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to update status'));
    }
  };

  const confirmRetire = async () => {
    if (!tenantId || !retiring) return;
    setRetireSubmitting(true);
    setError(null);
    try {
      await devicesApi.retireDevice(tenantId, retiring.id);
      setRetiring(null);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to retire device'));
    } finally {
      setRetireSubmitting(false);
    }
  };

  if (!tenantId) {
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role. If this is
        unexpected, sign out and back in to refresh your session.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <Typography variant="h4">Devices</Typography>
          <Typography variant="body2" color="text.secondary">
            Capture-app devices owned by your tenant. Participants are
            assigned to a device at session start.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <RefreshButton
            onClick={() => void reload()}
            refreshing={refreshing}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openEnroll}
            disabled={!canWrite}
          >
            Enroll device
          </Button>
        </Stack>
      </Stack>

      {!canWrite && (
        <Alert severity="info">
          Your admin account is <strong>read-only</strong>. You can view
          devices but can't enroll, rename, suspend or retire them.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tenantId && <InstallCaptureAppPanel tenantId={tenantId} />}

      {/* Fleet health summary — lifecycle + what needs attention. */}
      {devices && devices.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Chip label={`Total · ${fleet.total}`} sx={{ fontWeight: 700 }} />
          <Chip
            label={`Active · ${fleet.active}`}
            color="success"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={`Retired · ${fleet.retired}`}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
          {fleet.outdated > 0 && (
            <Chip
              label={`Update available · ${fleet.outdated}`}
              color="warning"
              sx={{ fontWeight: 700 }}
            />
          )}
          {fleet.offline > 0 && (
            <Chip
              label={`Offline · ${fleet.offline}`}
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
          )}
          {fleet.retired > 0 && (
            <FormControlLabel
              sx={{ ml: 'auto', mr: 0 }}
              control={
                <Switch
                  size="small"
                  checked={showRetired}
                  onChange={(e) => setShowRetired(e.target.checked)}
                />
              }
              label="Show retired"
            />
          )}
        </Stack>
      )}

      {devices === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : devices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No devices enrolled yet. Click <strong>Enroll device</strong>{' '}
            to generate an enrollment code for the first one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <SortHeader label="Label" col="label" sort={sort} />
                <SortHeader label="Status" col="status" sort={sort} />
                <SortHeader label="Location" col="room" sort={sort} />
                <SortHeader label="OS" col="os" sort={sort} />
                <SortHeader label="App" col="app" sort={sort} />
                <SortHeader label="Device ID" col="hardware" sort={sort} />
                <SortHeader label="Last seen" col="last_seen" sort={sort} />
                <SortHeader label="Enrolled" col="enrolled" sort={sort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleDevices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2, textAlign: 'center' }}
                    >
                      All devices are retired. Turn on{' '}
                      <strong>Show retired</strong> to see them.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visibleDevices.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">{d.label}</Typography>
                      <Tooltip title={`Enrollment id: ${d.id}`}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {d.id.slice(0, 8)}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={d.status} />
                  </TableCell>
                  <TableCell>{roomLabel(d)}</TableCell>
                  <TableCell>{prettyOs(d)}</TableCell>
                  <TableCell>
                    {d.app_version ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {d.app_version}
                        </Typography>
                        {/* Freshness badge only for in-service (active)
                            devices — a retired one won't be updated. */}
                        {d.status === 'active' &&
                          appVersionStatus(d.app_version, currentAppVersion) ===
                            'current' && (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="success"
                              label="Current"
                            />
                          )}
                        {d.status === 'active' &&
                          appVersionStatus(d.app_version, currentAppVersion) ===
                            'outdated' && (
                            <Chip
                              size="small"
                              color="warning"
                              label="Update available"
                            />
                          )}
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Physical device id (stable OS identifier) — the same
                        tablet carries this across every tenant it's enrolled
                        in, so support can recognise it. Blank until the device
                        checks in on an identity-aware build. */}
                    {d.hardware_id ? (
                      <Tooltip title="Physical device ID — the same tablet carries this in every tenant it's enrolled in">
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {d.hardware_id}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Connectivity: relative last-seen with an online dot
                        (green when checked in within ~5 min). */}
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: isOnline(d.last_seen_at)
                            ? 'success.main'
                            : 'text.disabled',
                        }}
                      />
                      <Typography
                        variant="body2"
                        color={
                          isOnline(d.last_seen_at)
                            ? 'text.primary'
                            : 'text.secondary'
                        }
                      >
                        {timeAgo(d.last_seen_at)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {formatDate(d.enrolled_at)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Sensor health">
                      <IconButton
                        size="small"
                        onClick={() => setHealthDevice(d)}
                        aria-label={`Sensor health for ${d.label}`}
                      >
                        <MonitorHeartIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {d.status !== 'retired' && (
                      <Tooltip title="Share with your other tenants">
                        <IconButton
                          size="small"
                          onClick={() => setSharingDevice(d)}
                          aria-label={`Share ${d.label}`}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {d.status !== 'retired' && canWrite && (
                      <>
                        <Tooltip title="Rename">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(d)}
                            aria-label={`Rename ${d.label}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {d.status === 'active' ? (
                          <Tooltip title="Suspend">
                            <Button
                              size="small"
                              onClick={() => void toggleStatus(d, 'suspended')}
                            >
                              Suspend
                            </Button>
                          </Tooltip>
                        ) : d.status === 'suspended' ? (
                          <Tooltip title="Re-activate">
                            <Button
                              size="small"
                              onClick={() => void toggleStatus(d, 'active')}
                            >
                              Activate
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Found — mark active again">
                            <Button
                              size="small"
                              onClick={() => void toggleStatus(d, 'active')}
                            >
                              Mark found
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Retire">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRetiring(d)}
                            aria-label={`Retire ${d.label}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeviceSharingDialog
        open={sharingDevice !== null}
        tenantId={tenantId}
        device={sharingDevice}
        canWrite={canWrite}
        onClose={() => setSharingDevice(null)}
        onChanged={() => void reload()}
      />

      <SensorHealthDialog
        open={healthDevice !== null}
        tenantId={tenantId}
        device={healthDevice}
        onClose={() => setHealthDevice(null)}
      />

      <Dialog open={enrollOpen} onClose={closeEnroll} fullWidth maxWidth="sm">
        <DialogTitle>Enroll device</DialogTitle>
        <DialogContent>
          {enrollResult === null ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Generate a one-time enrollment code. Open the
                capture-app on the target device, go to the
                "Enroll this device" screen, type the device's name
                in the Device label field there, and either scan the
                QR or type the code. The code expires in 10 minutes.
              </DialogContentText>
              {enrollError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {enrollError}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Code expires{' '}
                {formatTime(enrollResult.expires_at)}.
              </Alert>
              <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
                {/* QR encodes the raw enrollment code (not a URL,
                    not JSON) — the capture-app's existing input
                    parser then works identically whether the
                    operator types it or scans it. A white background
                    + small quiet-zone padding keeps scanning robust
                    on dark-mode displays. */}
                <Box
                  data-testid="enrollment-qr"
                  sx={{
                    p: 2,
                    bgcolor: 'common.white',
                    borderRadius: 1,
                    display: 'inline-flex',
                  }}
                >
                  <QRCodeSVG
                    value={enrollResult.code}
                    size={192}
                    level="M"
                    aria-label="Enrollment code QR"
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'monospace',
                    letterSpacing: 4,
                  }}
                >
                  {formatCode(enrollResult.code)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', maxWidth: 360 }}
                >
                  Scan the QR with the capture-app, or type the code
                  on the enrollment screen if the camera struggles.
                  The portal will only show this code once.
                </Typography>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEnroll} disabled={enrollSubmitting}>
            {enrollResult ? 'Close' : 'Cancel'}
          </Button>
          {!enrollResult && (
            <Button
              variant="contained"
              onClick={() => void submitEnroll()}
              disabled={enrollSubmitting}
            >
              {enrollSubmitting ? 'Generating…' : 'Generate code'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={editing !== null}
        onClose={() => (editSubmitting ? null : setEditing(null))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit device</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Label"
              fullWidth
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              disabled={editSubmitting}
            />
            <DialogContentText sx={{ mt: 1 }}>
              Default room — new recordings from this device are tagged with
              these automatically (an admin can still override per session).
            </DialogContentText>
            <SearchableSelect
              label="Default department"
              size="medium"
              fullWidth
              value={editDept}
              onChange={setEditDept}
              disabled={editSubmitting}
              pinned={NONE_OPTION}
              options={departments.map((x) => ({ value: x.id, label: x.name }))}
            />
            <SearchableSelect
              label="Default location"
              size="medium"
              fullWidth
              value={editLoc}
              onChange={setEditLoc}
              disabled={editSubmitting}
              pinned={NONE_OPTION}
              options={locations.map((x) => ({ value: x.id, label: x.name }))}
            />
            <TextField
              label="Edge appliance (cameras)"
              helperText="Paired on the capture app itself (the operator picks the box in the room). Read-only here."
              fullWidth
              value={
                appliances.find((a) => a.id === editAppliance)?.label ??
                '— not paired —'
              }
              slotProps={{ input: { readOnly: true } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={editSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitEdit()}
            disabled={editSubmitting || editLabel.trim() === ''}
          >
            {editSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={retiring !== null}
        onClose={() => (retireSubmitting ? null : setRetiring(null))}
      >
        <DialogTitle>Retire {retiring?.label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Retiring marks the device as no longer in service. The row
            stays so historical sessions still resolve, but the device
            won't appear in the active roster. There's no UI to
            un-retire — re-enroll a fresh device if you want one back.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetiring(null)} disabled={retireSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void confirmRetire()}
            color="error"
            variant="contained"
            disabled={retireSubmitting}
          >
            {retireSubmitting ? 'Retiring…' : 'Retire'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function StatusChip({ status }: { status: devicesApi.DeviceStatus }) {
  switch (status) {
    case 'active':
      return <Chip label="Active" size="small" color="success" />;
    case 'suspended':
      return <Chip label="Suspended" size="small" color="warning" />;
    case 'lost':
      return <Chip label="Lost" size="small" color="error" />;
    case 'retired':
      return <Chip label="Retired" size="small" />;
    default:
      return <Chip label={status} size="small" />;
  }
}

function formatCode(code: string): string {
  // Split an 8-char code as XXXX-XXXX. If we ever lengthen the code
  // this helper still does a single dash in the middle.
  const mid = Math.floor(code.length / 2);
  return `${code.slice(0, mid)}-${code.slice(mid)}`;
}
